// Module-level cache mapping group IDs to display names.
// Populated during navigation; survives within the SPA session.
const groupNameCache: Record<string, string> = {};

// In-flight fetch promises to avoid duplicate requests
const inflight: Record<string, Promise<string>> = {};

export function cacheGroupName(id: string, name: string) {
  groupNameCache[id] = name;
}

export function cacheGroups(groups: { id: string; name: string }[]) {
  groups.forEach(g => { groupNameCache[g.id] = g.name; });
}

export function getCachedGroupName(id: string): string | undefined {
  return groupNameCache[id];
}

export function buildBrowsePath(breadcrumb: { id: string }[]): string {
  return '/parts/browse/' + breadcrumb.map(g => g.id).join('/');
}

export function buildProductsPath(breadcrumb: { id: string }[]): string {
  return '/parts/products/' + breadcrumb.map(g => g.id).join('/');
}

/**
 * Fetch a group's name from the API if not already cached.
 * Deduplicates in-flight requests for the same ID.
 */
export function fetchAndCacheGroupName(
  makeAuthenticatedCall: <T>(fn: string, body?: any) => Promise<{ data?: T; error?: string }>,
  groupId: string
): Promise<string> {
  const cached = groupNameCache[groupId];
  if (cached) return Promise.resolve(cached);

  if (inflight[groupId]) return inflight[groupId];

  const promise = makeAuthenticatedCall<{ id: string; name: string }>('get-group-detail', { groupId })
    .then(({ data }) => {
      const name = data?.name || groupId;
      groupNameCache[groupId] = name;
      return name;
    })
    .catch(() => groupId)
    .finally(() => { delete inflight[groupId]; });

  inflight[groupId] = promise;
  return promise;
}
